import {
  BlotterTable,
  IconName,
  useBehaviorSubject,
  useBlotterTable,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  type ColumnDef,
  type CustomerQuote,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useMemo } from 'react';

interface CustomerQuotesToFixBlotterProps {
  customerQuotesToFix: CustomerQuote[];
}

export const CustomerQuotesToFixBlotter = ({ customerQuotesToFix }: CustomerQuotesToFixBlotterProps) => {
  const { observable: dataObservable } = useBehaviorSubject(
    () => ({ initial: true, data: customerQuotesToFix }),
    [customerQuotesToFix]
  );

  const { handleClickJson, jsonModal } = useJsonModal<CustomerQuote>();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const columns = useCustomerQuotesToFixColumns();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CustomerQuote>) => {
    return [
      {
        name: 'Show JSON',
        action: () => handleClickJson(params.node?.data),
        icon: `<i class="ag-icon ${IconName.Braces}"/>`,
      },
      'separator',
      ...getDefaultContextMenuItems(params),
    ];
  });

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable: undefined,
    rowID: 'RFQID' satisfies keyof CustomerQuote,
    columns,
    gridOptions: {
      suppressHeaderFocus: true,
      getContextMenuItems,
    },
  });

  return (
    <>
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
};

const useCustomerQuotesToFixColumns = () => {
  const columns = useMemo(
    () =>
      [
        /* The widths below are specifically set to fit well in the fixing dialog's blotter */
        { type: 'date', field: 'SubmitTime', title: 'Submit Time', width: 130 },
        { type: 'id', field: 'RFQID', width: 80 },
        { type: 'customer', title: 'Counterparty', field: 'Counterparty', width: 110 },
        { field: 'tradedSideOrSide', title: 'Side', type: 'quoteSide', width: 60 },
        { field: 'symbolOrSymbols', type: 'security', title: 'Symbol', width: 120 },
        { field: 'OrderQty', type: 'size', params: { currencyField: 'Currency' }, width: 120 },
        { field: 'fixingDate', type: 'dateOnly', title: 'Fixing Date', width: 110 },
        { field: 'PricingReference', type: 'price', params: { isReferencePrice: true }, width: 130 },
      ] satisfies ColumnDef<CustomerQuote>[],
    []
  );

  return columns;
};
